import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen,
  PlusCircle,
  History,
  IndianRupee,
  CheckCircle,
  AlertCircle,
  Library,
  RotateCcw,
  X
} from 'lucide-react';
import api from '../api';
import IssueBook from './IssueBook';
import ReturnBook from './ReturnBook';
import TransactionHistory from './TransactionHistory';

const Transactions = () => {
  const [activeTab, setActiveTab] = useState('issue');
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalIssued: 0,
    totalReturned: 0,
    totalOverdue: 0,
    totalFines: 0,
    pendingFines: 0,
    collectedFines: 0
  });

  const tenantId = localStorage.getItem('tenantId');

  // ============ FETCH STATS ============
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/transactions/stats', {
        params: { tenantId }
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [tenantId]);

  // ============ EFFECTS ============
  useEffect(() => {
    if (tenantId) {
      fetchStats();
    }
  }, [tenantId, fetchStats]);

  // Listen for refresh events from child components
  useEffect(() => {
    const handleRefresh = () => {
      fetchStats();
    };
    window.addEventListener('refreshStats', handleRefresh);
    return () => window.removeEventListener('refreshStats', handleRefresh);
  }, [fetchStats]);

  // ============ STATS CARDS ============
  const statCards = [
    { 
      label: 'Currently Issued', 
      value: stats.totalIssued || 0, 
      icon: BookOpen, 
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      borderColor: 'border-blue-200 dark:border-blue-800'
    },
    { 
      label: 'Total Returned', 
      value: stats.totalReturned || 0, 
      icon: CheckCircle, 
      color: 'emerald',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      borderColor: 'border-emerald-200 dark:border-emerald-800'
    },
    { 
      label: 'Overdue Books', 
      value: stats.totalOverdue || 0, 
      icon: AlertCircle, 
      color: 'rose',
      bgColor: 'bg-rose-50 dark:bg-rose-900/30',
      iconColor: 'text-rose-600 dark:text-rose-400',
      borderColor: 'border-rose-200 dark:border-rose-800'
    },
    { 
      label: 'Pending Fines', 
      value: `₹${stats.pendingFines || 0}`, 
      icon: IndianRupee, 
      color: 'amber',
      bgColor: 'bg-amber-50 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      borderColor: 'border-amber-200 dark:border-amber-800'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-slate-100 pb-12">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
        
        {/* Header Section */}
        <header className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 p-4 sm:p-6 transition-all">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-blue-50 dark:bg-blue-900/40 rounded-xl text-blue-600 dark:text-blue-400">
                  <Library className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Library Counter Desk
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    Manage book circulation, student returns, and track fine records
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 bg-slate-100 dark:bg-gray-700/60 p-1.5 rounded-xl border border-slate-200 dark:border-gray-600">
              <button
                onClick={() => setActiveTab('issue')}
                className={`py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 flex items-center justify-center space-x-1.5 ${
                  activeTab === 'issue' 
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-gray-600'
                }`}
              >
                <PlusCircle className="h-4 w-4" />
                <span>Issue</span>
              </button>
              
              <button
                onClick={() => setActiveTab('return')}
                className={`py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 flex items-center justify-center space-x-1.5 ${
                  activeTab === 'return' 
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20' 
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-gray-600'
                }`}
              >
                <RotateCcw className="h-4 w-4" />
                <span>Return</span>
              </button>
              
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 flex items-center justify-center space-x-1.5 ${
                  activeTab === 'history' 
                    ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20' 
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-gray-600'
                }`}
              >
                <History className="h-4 w-4" />
                <span>History</span>
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Quick Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index} 
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border ${stat.borderColor} p-4 sm:p-5 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight">{stat.value}</p>
                  </div>
                  <div className={`p-2.5 sm:p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.iconColor}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Global Banner Error */}
        {error && (
          <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4 text-rose-700 dark:text-rose-400 flex items-center justify-between shadow-sm animate-fadeIn">
            <span className="flex items-center text-xs sm:text-sm font-medium">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 text-rose-600 dark:text-rose-400" />
              {error}
            </span>
            <button onClick={() => setError(null)} className="text-rose-700 dark:text-rose-400 hover:opacity-75 p-1">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Tab Content Panes */}
        <main className="transition-all duration-200">
          {activeTab === 'issue' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
              <IssueBook />
            </div>
          )}

          {activeTab === 'return' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
              <ReturnBook />
            </div>
          )}

          {activeTab === 'history' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
              <TransactionHistory />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Transactions;